'use client';

import { useSyncExternalStore } from 'react';
import { isProvinceCode } from '@/lib/place-url';

// "My city" (owner's approval, 2026-09-29): the city a visitor last chose or
// looked at, kept on the device. Nothing goes to the server; the site only
// offers it back - as the first button of the row of cities - and never
// redirects or filters by it.

export type MyCity = { city: string; province: string };

const KEY = 'rec_my_city';
const EVENT = 'my-city-update';

/** A stored value, or null when it is not a city and a province code. */
export function cleanMyCity(raw: unknown): MyCity | null {
  if (!raw || typeof raw !== 'object') return null;
  const { city, province } = raw as Record<string, unknown>;
  if (typeof city !== 'string' || typeof province !== 'string') return null;
  const name = city.replace(/[\u0000-\u001f<>]/g, '').replace(/\s+/g, ' ').trim();
  if (!name || name.length > 80 || !isProvinceCode(province)) return null;
  return { city: name, province: province.toUpperCase() };
}

const EMPTY = null;
let cache: { raw: string; value: MyCity | null } | null = null;

function snapshot(): MyCity | null {
  let raw = '';
  try {
    raw = localStorage.getItem(KEY) || '';
  } catch {
    return EMPTY;
  }
  if (cache && cache.raw === raw) return cache.value;
  let value: MyCity | null = null;
  try {
    value = raw ? cleanMyCity(JSON.parse(raw)) : null;
  } catch {
    value = null;
  }
  cache = { raw, value };
  return value;
}

function subscribe(onChange: () => void) {
  window.addEventListener(EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

export function setMyCity(place: MyCity) {
  const clean = cleanMyCity(place);
  if (!clean) return;
  try {
    localStorage.setItem(KEY, JSON.stringify(clean));
    window.dispatchEvent(new Event(EVENT));
  } catch {
    // storage off: it lasts for this page only
  }
}

export function clearMyCity() {
  try {
    localStorage.removeItem(KEY);
    window.dispatchEvent(new Event(EVENT));
  } catch {
    // nothing to clear
  }
}

/** The remembered city; null on the server and before the first render on the device. */
export function useMyCity(): MyCity | null {
  return useSyncExternalStore(subscribe, snapshot, () => EMPTY);
}
